import React, {createContext, useContext, useEffect, useMemo, useState, type ReactNode} from 'react';

interface BreadcrumbContextType {
    /** Label registered by the active detail page for its dynamic leaf crumb, or null. */
    dynamicLabel: string | null;
    setDynamicLabel: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({
    dynamicLabel: null,
    setDynamicLabel: () => {},
});

export const BreadcrumbProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const [dynamicLabel, setDynamicLabel] = useState<string | null>(null);
    const value = useMemo(() => ({dynamicLabel, setDynamicLabel}), [dynamicLabel]);

    return (
        <BreadcrumbContext.Provider value={value}>
            {children}
        </BreadcrumbContext.Provider>
    );
};

/** Current dynamic breadcrumb label (used by the header trail to override the current crumb). */
export const useDynamicCrumbLabel = (): string | null => useContext(BreadcrumbContext).dynamicLabel;

/**
 * Register {@code label} as the name of the current detail route's leaf crumb
 * (e.g. a dashboard name). Pass the fetched entity label; it clears on unmount
 * and while empty, so the header falls back to the route's static crumb.
 */
export const useDynamicCrumb = (label: string | null | undefined): void => {
    const {setDynamicLabel} = useContext(BreadcrumbContext);

    useEffect(() => {
        setDynamicLabel(label ?? null);
        return () => setDynamicLabel(null);
    }, [label, setDynamicLabel]);
};
